import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Input, DatePicker, Select, message, Checkbox, Row, Col, Typography, Alert, Tag } from "antd";
import { scheduleModelData } from "../services/ModelService";
import { Schedule, OperatingDays, Period, OPERATING_DAY_DEFAULTS, PREDEFINED_SCHEDULES, Exception } from "../model/schedule";
import { ulid } from "ulid";
import dayjs from "../dayjs";
import { capitalize } from "lodash";
import ScheduleName from "./ScheduleName";
import useAllSchedules from "../hooks/useAllSchedules";
import ScheduleCalendar from "./ScheduleCalendar";
import { ReactComponent as Trash } from '../assets/icons/Trash.svg';
import useAllCharterSchedules from "../hooks/useAllCharterSchedules";
const { RangePicker } = DatePicker;
const { Option } = Select;
const { Title } = Typography;

export default function CreateScheduleModal({ visible, onClose, onScheduleCreated, scheduleId }) {
  const [form] = Form.useForm();
  const { allCharterSchedules: allSchedules } = useAllCharterSchedules();
  const [loading, setLoading] = useState(false);
  const [operatingDays, setOperatingDays] = useState(new OperatingDays([]));
  const [schedulePeriods, setSchedulePeriods] = useState([]);
  const [excludedPeriods, setExcludedPeriods] = useState([]);
  const [includedSchedules, setIncludedSchedules] = useState([]);
  const [excludedSchedules, setExcludedSchedules] = useState([]);
  const [exceptions, setExceptions] = useState([]);
  const [runningDates, setRunningDates] = useState([]);
  const [validationError, setValidationError] = useState('');
  const [hasSubmitAttempt, setHasSubmitAttempt] = useState(false);
  const [exceptionAddType, setExceptionAddType] = useState('custom');
  const [exceptionAddSourceIdx, setExceptionAddSourceIdx] = useState(undefined);

  useEffect(() => {
    if (scheduleId && allSchedules) {
      const schedule = new Schedule(allSchedules[scheduleId]);
      if (schedule) {
        setOperatingDays(schedule.operatingDays);
        setSchedulePeriods(schedule.schedulePeriods);
        setExcludedPeriods(schedule.excludedPeriods);
        setIncludedSchedules(schedule.includedSchedules);
        setExcludedSchedules(schedule.excludedSchedules);
        setExceptions(schedule.exceptions || []);
        setRunningDates(schedule.getRunningDates());
        form.setFieldsValue({
          scheduleName: schedule.scheduleName,
        });
      }
    }

    return () => {
      form.resetFields();
      setOperatingDays(new OperatingDays([]));
      setSchedulePeriods([]);
      setExcludedPeriods([]);
      setIncludedSchedules([]);
      setExcludedSchedules([]);
      setExceptions([]);
      setRunningDates([]);
    };
  }, [scheduleId, allSchedules]);

  useEffect(() => {
    const newSchedule = new Schedule({
      scheduleId: scheduleId || ulid(),
      scheduleName: form.getFieldValue('scheduleName'),
      charter: true,
      operatingDays: operatingDays,
      schedulePeriods: schedulePeriods,
      excludedPeriods: excludedPeriods,
      includedSchedules: includedSchedules,
      excludedSchedules: excludedSchedules,
      exceptions: exceptions
    })
    setRunningDates(newSchedule.getRunningDates(newSchedule.getFirstActiveDate()));
  }, [includedSchedules, excludedSchedules, excludedPeriods, schedulePeriods, operatingDays, exceptions]);

  const validateForm = () => {
    const hasName = form.getFieldValue('scheduleName');
    const hasValidSchedulePeriods = schedulePeriods?.length > 0 && schedulePeriods.every(period => period.isValid());
    const hasIncludedSchedules = includedSchedules?.length > 0;

    if (!hasName) {
      form.setFields([
        {
          name: 'scheduleName',
          errors: ['Schedule name is required']
        }
      ]);
      setValidationError('Schedule name is required');
      return false;
    }
    if (!hasValidSchedulePeriods && !hasIncludedSchedules) {
      form.setFields([
        {
          name: 'schedulePeriods',
          errors: ['Please add at least one valid schedule period or include a predefined schedule']
        }
      ]);
      setValidationError('Please add at least one valid schedule period or include a predefined schedule');
      return false;
    }
    setValidationError('');
    return true;
  };

  const handleSubmit = async () => {
    try {
      setHasSubmitAttempt(true);
      if (!validateForm()) {
        return;
      }
      const values = await form.validateFields();
      setLoading(true);

      const newSchedule = new Schedule({
        scheduleId: scheduleId || ulid(),
        scheduleName: values.scheduleName,
        charter: true,
        operatingDays: operatingDays,
        schedulePeriods: schedulePeriods,
        excludedPeriods: excludedPeriods,
        includedSchedules: includedSchedules,
        excludedSchedules: excludedSchedules,
        exceptions: exceptions
      });

      // Save the schedule
      await scheduleModelData.save(newSchedule);

      message.success("Schedule created successfully");
      form.resetFields();
      setOperatingDays(new OperatingDays([]));
      setSchedulePeriods([]);
      setExcludedPeriods([]);
      setIncludedSchedules([]);
      setExcludedSchedules([]);
      setExceptions([]);
      setRunningDates([]);

      if (onScheduleCreated) {
        onScheduleCreated(newSchedule);
      }

      onClose();
    } catch (error) {
      console.error("Error creating schedule:", error);
      message.error("Failed to create schedule");
    } finally {
      setLoading(false);
    }
  };

  const handleOperatingDaysChange = (value) => {
    if (value === 'none') return;
    setOperatingDays(new OperatingDays(OPERATING_DAY_DEFAULTS[value]));
  };

  const handleOperatingDayToggle = (day) => {
    const newOperatingDays = { ...operatingDays };
    newOperatingDays[day] = !newOperatingDays[day];
    setOperatingDays(new OperatingDays(newOperatingDays));
  };

  const addSchedulePeriod = () => {
    setSchedulePeriods([...schedulePeriods, new Period()]);
  };

  const updateSchedulePeriod = (index, dates) => {
    if (!dates) {
      // Remove the period if dates are cleared
      const newPeriods = [...schedulePeriods];
      newPeriods.splice(index, 1);
      setSchedulePeriods(newPeriods);
      return;
    }

    const newPeriods = [...schedulePeriods];
    newPeriods[index].update(dates);
    
    // Only keep the period if it's valid
    if (newPeriods[index].isValid()) {
      setSchedulePeriods(newPeriods);
    } else {
      newPeriods.splice(index, 1);
      setSchedulePeriods(newPeriods);
    }
  };

  const removeSchedulePeriod = (index) => {
    const newPeriods = [...schedulePeriods];
    newPeriods.splice(index, 1);
    setSchedulePeriods(newPeriods);
  };

  const addExcludedPeriod = () => {
    setExcludedPeriods([...excludedPeriods, new Period()]);
  };

  const updateExcludedPeriod = (index, dates) => {
    if (!dates) return;

    const newPeriods = [...excludedPeriods];
    newPeriods[index].update(dates);
    setExcludedPeriods(newPeriods);
  };

  const removeExcludedPeriod = (index) => {
    const newPeriods = [...excludedPeriods];
    newPeriods.splice(index, 1);
    setExcludedPeriods(newPeriods);
  };

  const handleAddSchedule = (scheduleId, isExcluded = false) => {
    const schedule = PREDEFINED_SCHEDULES[scheduleId];
    if (!schedule) return;

    if (isExcluded) {
      setExcludedSchedules([...excludedSchedules, schedule]);
    } else {
      setIncludedSchedules([...includedSchedules, schedule]);
    }
  };

  const removeIncludedSchedule = (index) => {
    const newSchedules = [...includedSchedules];
    newSchedules.splice(index, 1);
    setIncludedSchedules(newSchedules);
  };

  const removeExcludedSchedule = (index) => {
    const newSchedules = [...excludedSchedules];
    newSchedules.splice(index, 1);
    setExcludedSchedules(newSchedules);
  };

  // Helper to get all prefilled periods (from PREDEFINED_SCHEDULES only)
  const getPrefilledPeriods = () => {
    return Object.values(PREDEFINED_SCHEDULES)
      .filter(sched => sched.schedulePeriods?.length)
      .map(sched => ({
        label: sched.scheduleName,
        scheduleId: sched.scheduleId
      }));
  };

  // Helper to get all prefilled periods (from PREDEFINED_SCHEDULES only)
  const prefilledPeriods = getPrefilledPeriods();

  const handleAddException = (type) => {
    if (exceptionAddType === 'custom') {
      setExceptions([...exceptions, new Exception({ type })]);
    } else if (exceptionAddType === 'prefilled' && exceptionAddSourceIdx !== undefined) {
      const selected = prefilledPeriods[exceptionAddSourceIdx];
      if (selected && selected.scheduleId) {
        setExceptions([...exceptions, new Exception({ type, scheduleId: selected.scheduleId, source: selected.label })]);
      }
    }
    setExceptionAddType('custom');
    setExceptionAddSourceIdx(undefined);
  };

  const removeException = (index) => {
    const newExceptions = [...exceptions];
    newExceptions.splice(index, 1);
    setExceptions(newExceptions);
  };

  return (
    <Modal
      title="Create New Schedule"
      open={visible}
      onCancel={onClose}
      width={800}
      destroyOnClose={true}
      style={{ top: 30 }}
      footer={[
        <div className="d-flex justify-content-end align-items-center w-100" style={{gap: 10}}>
          {validationError && (
            <Alert
              message={validationError}
              type="error"
              style={{ width: '100%', textAlign: 'center' }}
            />
          )}
          <Button key="cancel" onClick={onClose}>
            Cancel
          </Button>
          <Button
            key="submit"
            type="primary"
            onClick={handleSubmit}
          >
            {scheduleId ? 'Update Schedule' : 'Create Schedule'}
          </Button>
        </div>
      ]}
    >
      <Form
        form={form}
        layout="vertical"
      >
        <Form.Item
          name="scheduleName"
          label="Schedule Name"
          rules={[{ required: true, message: 'Please enter a schedule name' }]}
        >
          <Input placeholder="Enter schedule name" />
        </Form.Item>

        {/* Base Calendar Section */}
        <Form.Item 
          label="Base Calendar"
          name="schedulePeriods"
          validateStatus={hasSubmitAttempt && schedulePeriods.length === 0 && includedSchedules.length === 0 ? 'error' : ''}
          help={hasSubmitAttempt && schedulePeriods.length === 0 && includedSchedules.length === 0 ? 'At least one base calendar period is required' : ''}
        >
          {schedulePeriods.map((period, index) => (
            <div key={index} style={{ display: 'flex', marginBottom: '8px' }}>
              <RangePicker
                value={[period.start, period.end]}
                onChange={(dates) => updateSchedulePeriod(index, dates)}
                format="DD/MM/YYYY"
                style={{ flex: 1 }}
              />
              <Button
                type="text"
                danger
                onClick={() => removeSchedulePeriod(index)}
                className="btn-icon-control btn-delete"
                style={{ marginLeft: '6px' }}
              ><Trash style={{ width: 14 }} /></Button>
            </div>
          ))}
          <Button onClick={addSchedulePeriod} className="btn-primary" block style={{ paddingTop: '6px', paddingBottom: '6px', height: 'auto', fontWeight: 500 }}>
            Add Base Calendar Period
          </Button>
        </Form.Item>

        {/* Operating Days Section */}
        <Form.Item label="Operating Days">
          <Select
            style={{ width: '100%', marginBottom: '16px' }}
            placeholder="Select operating days preset"
            onChange={handleOperatingDaysChange}
            value="none"
          >
            <Option value="none">Select operating days</Option>
            {Object.keys(OPERATING_DAY_DEFAULTS).map(type => (
              <Option key={type} value={type}>{type}</Option>
            ))}
          </Select>

          <div style={{ display: 'flex', flexWrap: 'wrap' }}>
            {Object.keys(operatingDays).map(day => (
              <div key={day} style={{ minWidth: '120px', width: '25%', marginBottom: '0px' }}>
                <Form.Item
                  valuePropName="checked"
                  style={{ marginBottom: 0 }}
                >
                  <Checkbox
                    checked={operatingDays[day]}
                    onChange={() => handleOperatingDayToggle(day)}
                  >
                    {capitalize(day)}
                  </Checkbox>
                </Form.Item>
              </div>
            ))}
          </div>
        </Form.Item>

        {/* Exceptions Section */}
        <Form.Item label="Exceptions">
          <Row gutter={20} style={{ marginBottom: '16px' }} align="middle">
            <Col>
              <Select
                value={exceptionAddType}
                style={{ width: 140, marginRight: 8 }}
                onChange={val => {
                  setExceptionAddType(val);
                  setExceptionAddSourceIdx(undefined);
                }}
              >
                <Option value="custom">Custom Range</Option>
                <Option value="prefilled">Prefilled Period</Option>
              </Select>
            </Col>
            {exceptionAddType === 'prefilled' && (
              <Col>
                <Select
                  showSearch
                  style={{ minWidth: 260, marginRight: 8 }}
                  placeholder="Select a period"
                  value={exceptionAddSourceIdx}
                  onChange={idx => setExceptionAddSourceIdx(idx)}
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {prefilledPeriods.map((item, idx) => (
                    <Option key={item.label} value={idx}>{item.label}</Option>
                  ))}
                </Select>
              </Col>
            )}
            <Col>
              <Button onClick={() => handleAddException('include')} className="btn-primary" style={{ marginRight: '8px' }} disabled={exceptionAddType === 'prefilled' && exceptionAddSourceIdx === undefined}>
                Add Include Exception
              </Button>
            </Col>
            <Col>
              <Button onClick={() => handleAddException('exclude')} className="btn-secondary" disabled={exceptionAddType === 'prefilled' && exceptionAddSourceIdx === undefined}>
                Add Exclude Exception
              </Button>
            </Col>
          </Row>

          {exceptions.map((exception, index) => (
            <div key={index} style={{ display: 'flex', marginBottom: '8px', alignItems: 'center' }}>
              <div style={{ marginRight: '8px', minWidth: '70px' }}>
                {capitalize(exception.type)}
                {exception.legacy && <Tag color="red" style={{marginLeft: 8}}>Legacy Excluded Period</Tag>}
                {exception.source && <Tag color="blue" style={{marginLeft: 8}}>{exception.source}</Tag>}:
              </div>
              {exception.period ? (
                <RangePicker
                  value={[exception.period.start, exception.period.end]}
                  onChange={(dates) => {
                    const newExceptions = [...exceptions];
                    newExceptions[index].period.update(dates);
                    // If user edits, clear the source tag
                    newExceptions[index].source = undefined;
                    setExceptions(newExceptions);
                  }}
                  format="DD/MM/YYYY"
                  style={{ flex: 1 }}
                />
              ) : (
                <span style={{ flex: 1 }}>{exception.source ? `All dates in ${exception.source}` : ''}</span>
              )}
              <Button
                type="text"
                danger
                onClick={() => removeException(index)}
                className="btn-icon-control btn-delete"
                style={{ marginLeft: '8px' }}
              ><Trash style={{ width: 14 }} /></Button>
            </div>
          ))}
        </Form.Item>

        <ScheduleCalendar
          schedulePeriods={schedulePeriods}
          operatingDays={operatingDays}
          excludedPeriods={[]} // No longer used in UI
          includedSchedules={includedSchedules}
          excludedSchedules={excludedSchedules}
          runningDates={runningDates}
          calendarTitle="Schedule Calendar Preview"
        />
      </Form>
    </Modal>
  );
} 